// 繁體中文字串常數
// 對應 PRD §16.3 預留接口（多語系）
//
// 全部使用者可見的文字都集中在這裡，未來要做 i18n 只要新增 zh-cn.ts / en.ts，
// UI 與 Scene 都不該寫死字串字面值，一律 import ZH_TW 使用。
//
// 設計原則：
// - 結構化分組（app / home / settings / game / result / scoring）方便定位
// - 巢狀深度盡量 ≤3，太深的 path 不好讀
// - as const 鎖型別，TI18nDict 可以推斷出每個 key 的字面型別

export const ZH_TW = {
  // 應用程式層級常數
  app: {
    title: '至尊麻將王',
  },

  // 首頁（CHomeScene）
  home: {
    start: '開始遊戲',
    settings: '設定',
  },

  // 設定畫面（音效、出牌秒數）
  settings: {
    title: '設定',
    audio: '音效',
    bgm: '背景音樂',
    doubleTapDiscard: '點兩下才出牌',
    discardTimeout: '出牌秒數',
    // 出牌秒數選項（對應 PRD §5.3）
    discardTimeoutOptions: {
      8: '8 秒',
      15: '15 秒',
      30: '30 秒',
      none: '不催促',
    },
    on: '開',
    off: '關',
  },

  // 牌局主畫面（CGameScene）
  game: {
    you: '你', // 玩家自家標記，PRD §9.2「東・你・」
    dealer: '莊', // 莊家標記
    // 依 seat index 的中文稱謂（0=玩家自家、順時針）。對應 core/types TSeat 註解。
    seatLabels: ['你', '右家', '對家', '左家'],
    // 四門風的顯示文字
    winds: {
      east: '東',
      south: '南',
      west: '西',
      north: '北',
    },
    // 動作按鈕文字（吃／碰／槓／胡／過／聽／代打／出牌）
    // 顯示優先序見 PRD §9.5
    actions: {
      chi: '吃',
      pong: '碰',
      kong: '槓',
      hu: '胡',
      pass: '過',
      tenpai: '聽',
      discard: '出牌',
      aiProxy: '代打',
    },
    // 各種畫面提示文字
    hint: {
      thinking: '思考中…', // AI 思考中圖示旁的文字
      tenpai: '聽牌', // 玩家聽牌時右側標題
      tenpaiTiles: '聽：', // 「聽：1萬 4萬」前綴
      aiProxyOn: 'AI 代打中', // 玩家位置代打狀態標記
      // 倒數秒數動態字串（最後 5 秒會變紅，邏輯在 UI 層處理）
      timeRemaining: (s: number) => `剩 ${s} 秒`,
      rotateDevice: '請將裝置轉為橫向', // OrientationMask 直式遮罩文字
    },
    pause: {
      title: '牌局暫停',
      continue: '繼續牌局',
      settings: '設定',
      backHome: '返回首頁',
      settingsTitle: '牌局設定',
      timerOff: '關閉計時',
      back: '返回',
    },
  },

  // 結算畫面（CResultScene）
  result: {
    win: '胡牌',
    lose: '放槍',
    unknownLoser: '未知', // 放槍者資料缺失時的保底顯示（不誤標成玩家自家）
    selfDraw: '自摸',
    draw: '流局',
    pointsDetail: '台數明細',
    rematch: '再來一局',
    backHome: '回首頁',
  },

  // 台數名稱（台型體系對齊原版至尊麻將王 PointCalculator，見 core/scoring.ts）
  // 結算明細會逐項列出每個適用台型 + 其台數
  scoring: {
    // 莊家 / 連莊
    dealer: '莊家',
    lianzhuang: '連莊',
    // 門清系
    menqing: '門清',
    buqiuren: '不求人',
    selfDraw: '自摸',
    // 番牌
    zifengke: '自風刻',
    quanfengke: '圈風刻',
    dragonZhong: '紅中刻',
    dragonFa: '青發刻',
    dragonBai: '白板刻',
    // 等待型態
    bianzhang: '邊張',
    kanzhang: '嵌張',
    dandiao: '單吊',
    quanqiuren: '全求人',
    // 暗刻系
    sananke: '三暗刻',
    siananke: '四暗刻',
    wuananke: '五暗刻',
    // 牌型
    pinghu: '平胡',
    pengpenghu: '碰碰胡',
    hunyise: '混一色',
    qingyise: '清一色',
    wuziwuhua: '無字無花',
    xiaosanyuan: '小三元',
    dasanyuan: '大三元',
    xiaosixi: '小四喜',
    dasixi: '大四喜',
    ziyise: '字一色',
    // 槓 / 海底 / 河底
    gangshang: '槓上開花',
    qianggang: '搶槓',
    haidi: '海底撈月',
    hedi: '河底撈魚',
    // 花牌
    flower: '正花',
    flowerKong: '花槓',
    bayian: '八仙過海',
    // 聽牌宣告
    tenpai: '聽牌',
  },
} as const;

// 從 ZH_TW 推斷出整個字典的型別，未來新增其他語系時可確保 key 一致
export type TI18nDict = typeof ZH_TW;
